import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useAuth, authorizedAxios } from "@moonscape/shared";

type TaxCategoryReport = {
  userid: string;
  gain: number;
  total_proceeds: number;
  total_cost: number;
  disposals: number;
  total_gain: number;
  total_loss: number;
  allowance_used: number;
  taxable_gain: number;
  proceeds_warning: boolean;
  cgt_rate: number;
  estimated_tax_due: number;
  tax_year: string;
  created_at: string;
  updated_at: string;
  disposal_type: string;
  allowance: number;
};

type TaxSummaryTotal = {
  totalGain: number;
  totalLoss: number;
  totalProceeds: number;
  totalCost: number;
  estimatedTaxDue: number;
  disposals: number;
  gain: number;
};

export type TaxSummary = {
  capitalGains: TaxCategoryReport | null;
  income: TaxCategoryReport | null;
  contractIncome: TaxCategoryReport | null;
  total: TaxSummaryTotal;
  taxYear: number;
};

export const useTaxSummary = ({
  taxYear,
  enabled = true,
  versionId,
}: {
  taxYear: number;
  enabled?: boolean;
  versionId?: number | null;
}): {
  isLoading: boolean;
  isIdle: boolean;
  error: Error | null;
  data: TaxSummary | undefined;
  refetch: () => void;
} => {
  const { getToken, user } = useAuth();

  const getTaxSummary = async (): Promise<TaxSummary> => {
    let url = `/tax/summary/${taxYear}`;
    if (versionId) {
      url += `?versionId=${versionId}`;
    }

    try {
      const token = await getToken();
      const res = await authorizedAxios(token).get(url);

      console.log("[useTaxSummary] response", res?.data);
      return res.data;
    } catch (error: any) {
      // 403 subscription_required / tax_year_locked — return undefined so UI shows paywall, not error
      if (
        error?.response?.status === 403 &&
        ["subscription_required", "tax_year_locked"].includes(error?.response?.data?.error)
      ) {
        return undefined as any;
      }
      console.error("[useTaxSummary] error", error);
      throw error;
    }
  };

  const {
    isLoading,
    isIdle,
    error,
    data,
    refetch,
  }: UseQueryResult<TaxSummary, Error> = useQuery({
    queryKey: ["tax_summary", taxYear, versionId],
    queryFn: getTaxSummary,
    enabled: !!user?.email && enabled,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    keepPreviousData: true,
  });

  return { isLoading, isIdle, error, data, refetch };
};
